import { useMemo, useState } from 'react';

import { AddIcon, Button, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useModal, useStableCallback } from '~/hooks/common';
import { useGetCollectingRequestDetailMutation } from '~/hooks/delivery-agency/queries';
import { getDeliveryAgencyCollectingRequestsColumnDefs } from '~/utils/delivery-agency/table';

import { DELIVERY_AGENCY_PAGE_SIZE } from '~/constants/delivery-agency';

import CollectingRequestDetailModal from '../CollectingRequestDetailModal';

import type { TypePagination } from '~/types/common.types';
import type { CollectingRequestDetail, CollectingRequestListItem } from '~/types/delivery-agency.types';

import styles from './CollectingRequestTable.module.scss';

const getRowId = (row: CollectingRequestListItem) => row.collectingRequestCode;

const CollectingRequestTable = ({
  data,
  pagination,
  selectedRows,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickRegister,
  onClickCancel,
}: {
  data: CollectingRequestListItem[] | null;
  pagination: TypePagination;
  selectedRows: CollectingRequestListItem[];
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (rows: CollectingRequestListItem[]) => void;
  onClickRegister?: () => void;
  onClickCancel?: () => void;
}) => {
  const detailModal = useModal();
  const [detailData, setDetailData] = useState<CollectingRequestDetail | null>(null);

  const detailMutation = useGetCollectingRequestDetailMutation({ showGlobalDimOverlay: false });

  const handleClickViewDetail = useStableCallback((collectingRequest: CollectingRequestListItem) => {
    detailMutation.mutate(
      { collectingRequestCode: collectingRequest.collectingRequestCode },
      {
        onSuccess: (detail) => {
          setDetailData(detail);
          detailModal.handleOpenModal();
        },
      }
    );
  });

  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyCollectingRequestsColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
        onClickViewDetail: handleClickViewDetail,
      }),
    [pagination.currentPage, pagination.pageSize, handleClickViewDetail]
  );

  // 같은 조건 재조회처럼 데이터 참조가 그대로면 표가 선택을 유지하므로, 선택을 부모 상태로 제어한다
  const selectedRowIds = useMemo(() => selectedRows.map(getRowId), [selectedRows]);

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              status="error"
              disabled={selectedRows.length === 0 || !onClickCancel}
              onClick={onClickCancel}
            >
              반품 취소
            </Button>
            <Button
              size="tiny"
              color="secondary"
              disabled={!onClickRegister}
              leftIcon={<AddIcon size={16} />}
              onClick={onClickRegister}
            >
              반품 신청
            </Button>
          </div>
        }
      />

      <div className={styles['table-wrapper']}>
        <Table
          data={data}
          getRowId={getRowId}
          columns={columnDefs}
          rowStatusType={data === null ? 'INIT' : 'COLLECTING_REQUEST_EMPTY'}
          selection={{ selectedRowIds, onChange: onChangeRowSelection }}
        />
      </div>

      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
      />

      {detailModal.isOpen && detailData && (
        <CollectingRequestDetailModal
          open={detailModal.isOpen}
          data={detailData}
          onClose={detailModal.handleCloseModal}
        />
      )}
    </div>
  );
};

export default CollectingRequestTable;
